import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Globe, Loader2, Search, AlertTriangle } from "lucide-react";
import { ChamberPicker } from "./ChamberPicker";
import { WebResults } from "./WebResults";

const UrlForm = ({ onResults, onChamber }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const extract = async () => {
    if (!url.trim()) { toast.error("Paste a directory or website URL"); return; }
    setLoading(true); setError("");
    try {
      const r = await api.post("/leads/web-extract", { url });
      onResults(r.data);
      if (!r.data.count) setError("No businesses found on that page — try a directory or listing page.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read that page");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.org/members"
          onKeyDown={(e) => e.key === "Enter" && !loading && extract()} data-testid="web-import-url" />
        <Button onClick={extract} disabled={loading} data-testid="web-import-extract">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {error && (
        <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="web-import-error">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
          <div className="space-y-1.5">
            <p>{error}</p>
            {url.includes("windsoressexchamber") && (
              <button onClick={onChamber} className="text-primary hover:underline font-medium" data-testid="web-import-go-chamber">
                Open the Chamber directory tab →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function WebImportDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("chamber");
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState({});
  const [enrich, setEnrich] = useState(true);
  const [importing, setImporting] = useState(false);

  const show = (data) => {
    setResults(data);
    setPicked(Object.fromEntries((data.businesses || []).map((_, i) => [i, true])));
  };
  const switchTab = (t) => { setTab(t); setResults(null); setPicked({}); };

  const doImport = async () => {
    const chosen = (results?.businesses || []).filter((_, i) => picked[i]);
    setImporting(true);
    try {
      const r = await api.post("/leads/web-import", { businesses: chosen, enrich });
      const { created, enriching } = r.data;
      toast.success(`Imported ${created} lead${created === 1 ? "" : "s"}`, {
        description: enriching ? `Looking up phone & website for ${enriching} on Google Maps in the background.` : undefined,
      });
      const refresh = () => {
        qc.invalidateQueries({ queryKey: ["leads"] });
        qc.invalidateQueries({ queryKey: ["lead-facets"] });
      };
      refresh();
      if (enriching) setTimeout(refresh, 20000);
      onOpenChange(false); switchTab("chamber");
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) switchTab("chamber"); }}>
      <DialogContent className="bg-popover border-border max-w-xl max-h-[92vh] overflow-y-auto" data-testid="web-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Import from the web</DialogTitle>
          <DialogDescription>
            Pull businesses from the Windsor Essex Chamber directory, or paste any listing page and let the AI extract them.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={switchTab}>
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="chamber" data-testid="web-import-tab-chamber">Chamber directory</TabsTrigger>
            <TabsTrigger value="web" data-testid="web-import-tab-web">Any web page</TabsTrigger>
          </TabsList>
          <TabsContent value="chamber" className={results ? "hidden" : ""}>
            <ChamberPicker onResults={show} />
          </TabsContent>
          <TabsContent value="web">
            <UrlForm onResults={show} onChamber={() => switchTab("chamber")} />
          </TabsContent>
        </Tabs>
        {results && tab === "chamber" && (
          <button onClick={() => setResults(null)} className="text-xs text-primary hover:underline text-left" data-testid="chamber-back-btn">
            ← Change categories
          </button>
        )}
        {results && results.count > 0 && (
          <WebResults results={results} picked={picked} setPicked={setPicked} enrich={enrich}
            setEnrich={setEnrich} importing={importing} onImport={doImport} />
        )}
      </DialogContent>
    </Dialog>
  );
}
